/**
 * Creates a cached set of actions per key (e.g. per cell, per row, per tab).
 *
 * You give it a factory that builds the actions for one key. It returns a
 * getter: the first call for a key runs the factory, every later call for
 * that key returns the exact *same object*. No React involved — you can call
 * it from components, event handlers, tests, or plain JS.
 *
 * Why the "same object" part matters:
 *
 * - One actions object per key, no matter how many components use it.
 * - Safe to pass to `React.memo` children and to effect deps — it never changes
 *   identity, so it never causes extra re-renders.
 *
 * How to use it:
 *
 * - Call it once at the top level of a module and export the result. Don't call
 *   it inside components — that would rebuild the cache on every render.
 * - The factory should only capture the key. Read live data (stores, props)
 *   _inside_ the action bodies, at call time. If the factory captures data
 *   objects, the cache keeps them alive forever, leading to memory leaks.
 *
 * @example
 *   // gridCellActions.ts — note: top level of the module
 *   export const getCellActions = bindActionsByKey((cellKey: string) => ({
 *     edit: () => {
 *       gridState.editingCell = cellKey
 *     },
 *     stopEditing: () => {
 *       gridState.editingCell = null
 *     },
 *   }))
 *
 *   // In React:
 *   const { select } = getCellActions(cellKey)
 *   // In plain JS:
 *   getCellActions(nextCell(cellKey)).select()
 *
 * @param factory - Builds the actions object for one key. Runs at most once
 *   per distinct key.
 *
 * @returns A getter `(key) => Actions`. Also exposes `.delete(key)`,
 *   `.clear()` and `.size` for cache management.
 *
 * @warning If two features could share key formats (two grids both using "r3c7"),
 * namespace the keys (`${gridId}:${cellKey}`) so they don't collide.
 *
 * @warning The cache never cleans itself up. Entries stay for the lifetime of the
 * app, even after the components that used them unmount. That's fine when
 * keys are limited (a grid's cells, a form's fields). If keys can grow
 * without limit (e.g. ids from paginated server data), or if you just want
 * the cache to live only as long as the feature, clear it when the feature
 * unmounts:
 *
 * ```tsx
 * function GridPage() {
 *   // wipe the cache when the user leaves the grid
 *   useEffect(() => () => getCellActions.clear(), [])
 *   ...
 * }
 * ```
 */
export function bindActionsByKey<Key, Actions>(factory: (key: Key) => Actions) {
  const cache = new Map<Key, Actions>()

  const get = (key: Key): Actions => {
    let actions = cache.get(key)
    if (actions === undefined) {
      actions = factory(key)
      cache.set(key, actions)
    }
    return actions
  }

  get.delete = (key: Key) => cache.delete(key)
  get.clear = () => cache.clear()
  Object.defineProperty(get, 'size', { get: () => cache.size })

  return get as typeof get & { readonly size: number }
}
